import type { BackendModule, FlatNamespace, i18n, Namespace, Resource, TFunction } from "i18next"

import chalk from "chalk"
import { createInstance } from "i18next"
import { initReactI18next } from "react-i18next/initReactI18next"

import { i18nConfig } from "#environment/i18nConfig"

export type $NormalizeIntoArray<T extends unknown | readonly unknown[]> = T extends readonly unknown[] ? T : [T]

// Gets the type of an array or the type itself if not an array
type InferArrayValuesElseReturnType<T> = T extends (infer A)[] ? A : T
export type AllNameSpaces = FlatNamespace

// Accepts an array of namespaces as generic argument - the first one is the default namespace (without namespace prefix), the rest is not
export type CustomTypedTFunction<DefaultNamespace extends Namespace, Namespaces extends AllNameSpaces> = TFunction<
  [...$NormalizeIntoArray<DefaultNamespace>, ...Exclude<Namespaces, InferArrayValuesElseReturnType<DefaultNamespace>>[]]
>

/**
 * Extracts client resources from all loaded resources
 * Example:
 * {
 *   de: {
 *     'cp/basicArtistApply': {...}
 *     'cp/basicArtistApply-client': { ... }
 *   }
 * }
 * --> extracts 'cp/basicArtistApply-client'
 */
export function getClientResources(resources: Resource, throwIfNoClientResources = false) {
  const locales = Object.keys(resources) as (keyof Resource)[]
  const clientNameSpaces: AllNameSpaces[] = []

  // iterate every locale as key of resources and then from the value of the locales only keep the entries that have "-client" in their key
  const clientResources = locales.reduce((acc, locale) => {
    const resourcesForLocale = resources[locale]
    // @ts-ignore
    const clientResources = Object.entries(resourcesForLocale).filter(([key]) => {
      if (key.includes("-client") && !clientNameSpaces.includes(key as any)) {
        clientNameSpaces.push(key as any)
        return true
      }
      return false
    })

    // Locale doesnt contain client resource --> omit locale
    if (clientResources.length === 0) return acc

    return { ...acc, [locale]: Object.fromEntries(clientResources) }
  }, {})

  if (throwIfNoClientResources && Object.keys(clientResources).length === 0) {
    throw new Error(chalk.bold.red("======================== NO CLIENT RESOURCES FOUND FOR PROVIDER ========================"))
  }

  return { clientResources, clientNameSpaces }
}

export type ClientTrans = {
  clientResources: Resource
  clientNameSpaces: AllNameSpaces[]
  locale: string
}

export async function rawInitTranslations<TNamespace extends AllNameSpaces[]>({
  locale,
  namespaces,
  i18nInstance,
  resources,
  resourceLoader,
}: {
  locale: string
  namespaces: TNamespace
  i18nInstance?: i18n
} & ({ resourceLoader: BackendModule<object>; resources?: Resource } | { resources: Resource; resourceLoader?: BackendModule<object> })) {
  i18nInstance = i18nInstance ?? createInstance()

  i18nInstance.use(initReactI18next)

  if (!resources && resourceLoader) {
    i18nInstance.use(resourceLoader)
  }

  await i18nInstance.init({
    lng: locale,
    resources,
    fallbackLng: i18nConfig.defaultLocale,
    supportedLngs: i18nConfig.locales,
    defaultNS: namespaces[0],
    fallbackNS: namespaces[0],
    ns: namespaces,
    preload: resources ? [] : i18nConfig.locales,
  })

  const { clientResources, clientNameSpaces } = getClientResources(i18nInstance.services.resourceStore.data)

  return {
    i18n: i18nInstance,
    resources: i18nInstance.services.resourceStore.data,
    clientTrans: {
      clientResources,
      clientNameSpaces,
      locale,
    } satisfies ClientTrans,
    locale,
    // TNamespace[number] converts array type (keyof Resources)[] to union type ResourceKeyA | ResourceKeyB ...
    t: i18nInstance.t as CustomTypedTFunction<TNamespace[0], TNamespace[number]>,
  }
}
